import { engineWorkspaces, type StaffSession } from '@myfm/domain';
import { withBuildingScope } from './building-scope';
import {featureWorkspacePage} from './feature-workspace-navigation';

/** Master design v3: taxonomy and links only; permissions and records stay canonical. */
export const workspaceGroups = [
  { label: 'Clients & buildings', codes: ['new_business', 'onboarding', 'property_admin'] },
  { label: 'Property operations', codes: ['repairs', 'operations_compliance', 'assets', 'insurance'] },
  { label: 'Business services', codes: ['finance', 'governance', 'communications', 'suppliers', 'people'] }
] as const;

export const workspaceDescriptions: Record<string, string> = {
  new_business: 'Enquiries, proposals and management contracts.',
  onboarding: 'Bring a building into management and prepare its handover.',
  property_admin: 'Properties, leases, ownership and the people connected to each building.',
  repairs: 'Move reported issues through assessment, instruction and accepted work.',
  operations_compliance: 'Scheduled visits, inspections, requirements and evidence.',
  assets: 'Building systems, locations and their retained history.',
  insurance: 'Policies, renewals, claims and insurance evidence.',
  finance: 'Budgets, charges, invoices, payments and reconciliation.',
  governance: 'Management authority, instructions and recorded decisions.',
  communications: 'Conversations, cases, responses and deadlines.',
  suppliers: 'Supplier records, capability, evidence and appointments.',
  people: 'Private staff records, working arrangements and availability.'
};

export function permittedWorkspaces(session: StaffSession) {
  if (!session.permittedDestinations.some(item => item.id === 'engines')) return [];
  const building = session.effectiveScope.kind === 'building' ? session.effectiveScope.id : undefined;
  return engineWorkspaces.filter(engine => engine.links.some(link => link.permissions.every(permission => session.capabilities.includes(permission))))
    .map(engine => ({ ...engine, href: withBuildingScope('/engines/' + engine.code, engine.links.every(link=>link.portfolioOnly)?undefined:building), description: workspaceDescriptions[engine.code] ?? '' }));
}

/** Classify existing source routes without rewriting a record's URL or identity. */
export function workspaceCode(pathname: string): string | undefined {
  const feature=featureWorkspacePage(pathname);if(feature)return feature.workspace;
  const engine = /^\/engines\/([a-z_]+)(?:\/|$)/.exec(pathname)?.[1];
  if (engine) return engine;
  if (pathname.startsWith('/enquiries')) return 'new_business';
  if (/^\/buildings\/[^/]+\/(?:onboarding|setup|mobilisation)(?:\/|$)/.test(pathname)) return 'onboarding';
  if (pathname.startsWith('/workforce')) return 'people';
  if (pathname.startsWith('/work/supplier') || pathname.startsWith('/work/contractor') || pathname.startsWith('/work/service-provider')) return 'suppliers';
  if (pathname.startsWith('/finance/')) return 'finance';
  if (pathname.startsWith('/insurance')) return 'insurance';
  if (/\/building-safety\//.test(pathname)) return 'operations_compliance';
  if (/\/governance(?:\/|$)/.test(pathname)) return 'governance';
  if (pathname.startsWith('/communications')) return 'communications';
  if (pathname.startsWith('/contacts') || pathname.startsWith('/records/') || /\/properties(?:\/|$)/.test(pathname)) return 'property_admin';
  if (/^\/buildings\/[^/]+\/(?:structure|service-contracts|expected-services|included-services|utilities|assets|asset-import)(?:\/|$)/.test(pathname)) return 'assets';
  if (pathname.startsWith('/assets') || pathname.startsWith('/property-assets') || pathname.startsWith('/buildings/areas/')) return 'assets';
  if (pathname.startsWith('/compliance') || pathname.startsWith('/reports') || pathname.startsWith('/operations') || pathname.startsWith('/work/findings') || /\/building-safety\//.test(pathname)) return 'operations_compliance';
  if (pathname.startsWith('/work/') && !pathname.startsWith('/work/reviews') && !pathname.startsWith('/work/questions')) return 'repairs';
  return undefined;
}
